// components/BagLabelReplaceDialog.jsx
// Delete a bag label that dependent Customer Specs pin: pick a replacement label (or
// "No bag label"), which rewrites every dependent pin, then deletes — one transactional
// backend call (POST /api/baglabels/:id/replace-delete). Clone of CaseTagReplaceDialog.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, X } from 'lucide-react';
import TypeaheadSelect from './TypeaheadSelect';

const NO_LABEL_VALUE = '__NONE__';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--shadow-a35);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Dialog = styled.div`
  background: var(--white);
  border-radius: 16px;
  box-shadow: 0 24px 64px var(--shadow-a25);
  width: min(560px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 22px 22px 18px;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-a60);
  cursor: pointer;

  &:hover { background: var(--shadow-a05); color: var(--black); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 1.05rem;
  color: var(--color-danger);
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
`;

const Body = styled.p`
  margin: 0 0 14px;
  font-size: 0.92rem;
  color: var(--black);
`;

const DepBlock = styled.div`
  border: 1px solid var(--shadow-a08);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 14px;
`;

const DepHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-weight: 700;
  color: var(--black);
`;

const Badge = styled.span`
  background: var(--danger-a10);
  color: var(--color-danger);
  font-size: 0.78rem;
  font-weight: 800;
  border-radius: 999px;
  padding: 2px 10px;
  min-width: 26px;
  text-align: center;
`;

const DepList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: var(--ink-a60);
  font-size: 0.85rem;

  li + li { margin-top: 2px; }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ink-a70);
  margin: 4px 0 6px;
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const BtnSecondary = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 1px solid var(--shadow-a08);
  border-radius: 8px;
  background: var(--white);
  color: var(--black);
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--grey-100); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const BtnDanger = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: var(--color-danger);
  color: var(--white);
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--color-danger-hover); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

export default function BagLabelReplaceDialog({
  open,
  label,              // the bag label being deleted (row from /api/baglabels)
  dependents,         // { specs: { count, sample:[{id,name}], hasMore } }
  bagLabels = [],     // full label list, to build the "Replace with" options
  busy = false,
  onCancel,
  onConfirm,          // (replaceWith: number | null) => void
}) {
  const [replaceWith, setReplaceWith] = useState('');

  // Reset the selection whenever the dialog (re)opens for a label.
  useEffect(() => { if (open) setReplaceWith(''); }, [open, label]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel && onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const options = useMemo(() => {
    // Active labels only — reassigning pins to an inactive label would drift-drop
    // at render on every affected spec, making the replacement a silent no-op.
    const others = (bagLabels || []).filter(
      (bl) => bl.IsActive && String(bl.UniqueID) !== String(label?.UniqueID)
    );
    const labelOpts = others.map((bl) => ({
      value: String(bl.UniqueID),
      label: bl.LabelName || `Label #${bl.UniqueID}`,
    }));
    return [{ value: NO_LABEL_VALUE, label: '(No bag label)' }, ...labelOpts];
  }, [bagLabels, label]);

  if (!open || !label) return null;

  const count = dependents?.specs?.count || 0;
  const sample = dependents?.specs?.sample || [];
  const hasMore = dependents?.specs?.hasMore;
  const canConfirm = replaceWith !== '' && !busy;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm && onConfirm(replaceWith === NO_LABEL_VALUE ? null : Number(replaceWith));
  };

  return (
    <Backdrop onClick={onCancel} role="presentation">
      <Dialog onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="baglabel-replace-title">
        <CloseBtn type="button" onClick={onCancel} aria-label="Close dialog">
          <X size={16} aria-hidden="true" />
        </CloseBtn>

        <Title id="baglabel-replace-title">
          <AlertTriangle size={18} aria-hidden="true" />
          Delete bag label &ldquo;{label.LabelName}&rdquo;
        </Title>
        <Body>
          {count} customer specification{count === 1 ? '' : 's'} pin this bag label. Pick a
          replacement (or &ldquo;No bag label&rdquo;) to reassign {count === 1 ? 'it' : 'them'} to,
          then delete.
        </Body>

        <DepBlock>
          <DepHead>
            <Badge>{count}</Badge>
            Customer Specification{count === 1 ? '' : 's'}
          </DepHead>
          {sample.length > 0 && (
            <DepList>
              {sample.map((s) => (
                <li key={`s-${s.id}`}>{s.name || `Spec #${s.id}`}</li>
              ))}
              {hasMore && <li>…and more</li>}
            </DepList>
          )}
        </DepBlock>

        <FieldLabel htmlFor="baglabel-replace-with">Replace with</FieldLabel>
        <TypeaheadSelect
          ariaLabel="Replace with"
          value={replaceWith}
          placeholder="Select replacement…"
          size="lg"
          options={options}
          onChange={(v) => setReplaceWith(String(v || ''))}
        />

        <Foot>
          <BtnSecondary type="button" onClick={onCancel} disabled={busy}>Cancel</BtnSecondary>
          <BtnDanger type="button" onClick={handleConfirm} disabled={!canConfirm}>
            Reassign all {count} &amp; Delete
          </BtnDanger>
        </Foot>
      </Dialog>
    </Backdrop>
  );
}
